import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, ArrowRight, ChevronDown } from "lucide-react";
import usePriorityStates from "../../hooks/usePriorityStates";
import StateSelect from "../StateSelect";
import { fieldCls } from "../QuickMatchForm";

const chip = "rounded-full px-3.5 py-2 text-sm font-medium ring-1 transition-colors";

export default function StateFinder() {
  const s = usePriorityStates();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [showAll, setShowAll] = useState(false);
  const top = new Set([...s.high_priority, ...s.priority].map((x) => x.code));
  const rest = s.all_states.filter((x) => !top.has(x.code));

  return (
    <section className="py-16 md:py-24 bg-[#F8DCDB]/60" data-testid="priority-states-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#0F2A1F]/60 font-bold">Coast to coast</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F2A1F] leading-tight">Live anywhere in the US? You're in.</h2>
          <p className="mt-4 text-[#0F2A1F]/70 leading-relaxed">Every state has open roles. <strong className="text-[#0F2A1F]">Texas</strong> and <strong className="text-[#0F2A1F]">Oklahoma</strong> applicants are fast-tracked.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (code) nav(`/state/${code}`); }} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="state-finder-form">
            <StateSelect value={code} onChange={setCode} placeholder="Pick your state" className={`${fieldCls} bg-white sm:flex-1`} testId="state-finder-select" />
            <button type="submit" className="h-12 px-6 rounded-lg bg-[#0F2A1F] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#163b2c] transition-colors" data-testid="state-finder-go">
              See my jobs <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
        <div className="lg:col-span-7">
          <div className="flex flex-wrap gap-2.5">
            {s.high_priority.map((x) => (
              <Link key={x.code} to={`/state/${x.code}`} className="inline-flex items-center gap-2 rounded-xl bg-[#0F2A1F] text-white px-4 py-3 font-bold hover:bg-[#163b2c] transition-colors" data-testid={`home-hp-${x.code}`}>
                <Flame className="h-4 w-4 text-[#D4AF37]" /> {x.label} <span className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Top priority</span>
              </Link>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {s.priority.map((x) => (
              <Link key={x.code} to={`/state/${x.code}`} className={`${chip} bg-white text-[#0F2A1F] ring-[#0F2A1F]/10 hover:ring-[#0F2A1F]/40`} data-testid={`home-p-${x.code}`}>{x.label}</Link>
            ))}
            {showAll && rest.map((x) => (
              <Link key={x.code} to={`/state/${x.code}`} className={`${chip} bg-white/60 text-[#0F2A1F]/80 ring-[#0F2A1F]/10 hover:ring-[#0F2A1F]/40`} data-testid={`home-s-${x.code}`}>{x.label}</Link>
            ))}
          </div>
          {rest.length > 0 && (
            <button onClick={() => setShowAll((v) => !v)} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2A1F] hover:underline" data-testid="state-finder-toggle-all">
              {showAll ? "Show fewer states" : `Show all ${s.all_states.length} states`} <ChevronDown className={`h-4 w-4 transition-transform ${showAll ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
